import { useMutation, useQueryClient } from '@tanstack/react-query'
import { UpdateProviderStorePayload } from '../configure-store.types'
import { updatePreviewSettings } from '@/app/apis/Providers/requests'
import { useToast } from '@/shad/ui/toast/use-toast'
import { parseRequestError } from '@/common/utilities'
import { ProviderQueryKeys } from '@/queries/providers/queries'
import { useSelector } from 'react-redux'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { ProviderPreviewSettings } from '@/schemas/provider.schema'

interface UseUpdateStoreOptions {
  providerId: string
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export const useUpdatePreviewSettings = ({
  providerId,
  onSuccess,
  onError,
}: UseUpdateStoreOptions) => {
  const programId = useSelector(selectSelectedProgram).id
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (payload: UpdateProviderStorePayload) => {
      return await updatePreviewSettings(providerId, payload)
    },
    onSuccess: (data) => {
      // Update the cache with the returned data
      queryClient.setQueryData(
        ProviderQueryKeys.details(programId, providerId),
        (old: ProviderPreviewSettings | undefined) => ({
          ...old,
          ...data,
        })
      )
      queryClient.invalidateQueries({
        queryKey: ProviderQueryKeys.previewSettings(providerId, programId),
      })

      toast({
        title: 'Store Updated',
        description: 'Store information has been updated successfully.',
        variant: 'default',
      })

      onSuccess?.()
    },
    onError: (error: Error) => {
      const errorMessage = parseRequestError(error).message

      toast({
        title: 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      })

      onError?.(error)
    },
  })
}
